/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * InvestorNotificationService — Stateful notification management
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Manages the lifecycle of investor notifications:
 *   - Create from payloads (via mapping policy)
 *   - Ingest internal events (milestone, distribution, evidence, release)
 *   - Query by investor (all, unread, count)
 *   - Mark read (single / all)
 *
 * Follows the singleton pattern (getInvestorNotificationService / reset…).
 *
 * @module services/InvestorNotificationService
 */

import type {
    InvestorNotification,
    InvestorNotificationPayload,
} from '@/types/investorNotifications';
import {
    mapMilestoneEvent,
    mapDistributionEvent,
    mapEvidenceEvent,
    mapReleaseEvent,
    mapApprovalEvent,
    mapSignatureEvent,
    mapFinanceApprovalEvent,
    mapDataRoomAccessEvent,
    mapPortfolioEvent,
    type MilestoneEventInput,
    type DistributionEventInput,
    type EvidenceEventInput,
    type ReleaseEventInput,
    type PortfolioEventInput,
    type ApprovalEventInput,
    type SignatureEventInput,
    type FinanceApprovalEventInput,
    type DataRoomAccessEventInput,
} from '@/services/policies/investorNotificationPolicy';

// ═══════════════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════════════

export class InvestorNotificationService {
    private notifications: InvestorNotification[] = [];
    private counter = 0;
    private seeded = false;
    private listeners = new Set<() => void>();

    // ── DEMO SEED ────────────────────────────────────────────────────────

    /**
     * Seeds realistic demo notifications so the panel is never empty.
     * Safe to call multiple times — only runs once.
     */
    seedDemoData(investorIds: string[]): void {
        if (this.seeded) return;
        this.seeded = true;

        const now = Date.now();
        const h = (hours: number) => new Date(now - hours * 60 * 60 * 1000).toISOString();

        // Seed in reverse-chronological order (oldest first, newest last → unshift)
        const seed: Array<Omit<InvestorNotification, 'id'>> = [
            // ── 3 days ago ──────────────────────────────────────────
            {
                investorId: '',
                type: 'dataroom_access_granted',
                title: 'Data Room Access Granted',
                message: 'You have been granted view access to the Harbour District data room.',
                severity: 'info',
                projectId: '2',
                projectName: 'Harbour District Mixed-Use Development',
                referenceId: 'showcase-room-harbour',
                createdAt: h(72),
                read: true,
            },
            {
                investorId: '',
                type: 'portfolio_update',
                title: 'Portfolio Update',
                message: 'Your portfolio summary is available. Total committed capital across four positions: EUR 29.5M.',
                severity: 'info',
                createdAt: h(68),
                read: true,
            },
            // ── 2 days ago ──────────────────────────────────────────
            {
                investorId: '',
                type: 'approval_requested',
                title: 'Approval Requested',
                message: 'The Foundation works capital release for NorthLink has been submitted for approval.',
                severity: 'info',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-approval-northlink-release',
                createdAt: h(52),
                read: true,
            },
            {
                investorId: '',
                type: 'signature_requested',
                title: 'Signature Requested',
                message: 'The NorthLink Capital Gate Agreement has been sent for signature.',
                severity: 'info',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-signature-northlink-gate',
                createdAt: h(48),
                read: true,
            },
            // ── Yesterday ───────────────────────────────────────────
            {
                investorId: '',
                type: 'milestone_verified',
                title: 'Milestone Verified',
                message: 'The Foundation works Capital Gate in NorthLink has been independently verified.',
                severity: 'success',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'milestone-1-gate',
                createdAt: h(28),
                read: true,
            },
            {
                investorId: '',
                type: 'release_completed',
                title: 'Capital Release Completed',
                message: 'The EUR 3M final release for NovaScale European Expansion has been completed.',
                severity: 'success',
                projectId: '4',
                projectName: 'NovaScale European Expansion',
                referenceId: 'showcase-distribution-novascale-settlement',
                createdAt: h(26),
                read: true,
            },
            {
                investorId: '',
                type: 'approval_decided',
                title: 'Approval Completed',
                message: 'The NorthLink Foundation works capital release has been fully approved.',
                severity: 'success',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-approval-northlink-release',
                createdAt: h(24),
                read: false,
            },
            {
                investorId: '',
                type: 'signature_completed',
                title: 'Signature Completed',
                message: 'All required parties have completed the NorthLink Capital Gate Agreement.',
                severity: 'success',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-signature-northlink-gate',
                createdAt: h(22),
                read: false,
            },
            // ── Today ───────────────────────────────────────────────
            {
                investorId: '',
                type: 'evidence_available',
                title: 'Documentation Available',
                message: 'New Foundation works verification documentation is available for review in NorthLink.',
                severity: 'info',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-pack-northlink-foundation',
                createdAt: h(6),
                read: false,
            },
            {
                investorId: '',
                type: 'finance_approval_requested',
                title: 'Finance Approval Requested',
                message: 'The EUR 7.40M NorthLink capital release has been submitted for investor review.',
                severity: 'info',
                projectId: '1',
                projectName: 'NorthLink Regional Infrastructure Programme',
                referenceId: 'showcase-finance-northlink-release',
                entityRef: {
                    entityId: 'showcase-finance-northlink-release',
                    entityType: 'finance_approval',
                    workspaceId: 'showcase-workspace',
                    investmentId: 'showcase-investment-northlink',
                    projectId: '1',
                    sourceSystem: 'ax1-showcase',
                    asOf: h(4),
                    currency: 'EUR',
                    provenance: { authority: 'showcase-investor-activity', version: '1' },
                },
                createdAt: h(4),
                read: false,
            },
        ];

        for (const investorId of investorIds) {
            for (const item of seed) {
                const notification: InvestorNotification = {
                    id: `inv-notif-${++this.counter}`,
                    ...item,
                    investorId,
                };
                this.notifications.push(notification);
            }
        }
        // Sort newest-first
        this.notifications.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        this.emit();
    }

    // ── CRUD ─────────────────────────────────────────────────────────────

    /**
     * Create a notification from a mapped payload.
     */
    createNotification(
        investorId: string,
        payload: InvestorNotificationPayload,
    ): InvestorNotification {
        const notification: InvestorNotification = {
            id: `inv-notif-${++this.counter}`,
            investorId,
            type: payload.type,
            title: payload.title,
            message: payload.message,
            severity: payload.severity,
            projectId: payload.projectId,
            projectName: payload.projectName,
            referenceId: payload.referenceId,
            entityRef: payload.entityRef,
            createdAt: new Date().toISOString(),
            read: false,
        };
        this.notifications.unshift(notification);
        this.emit();
        return notification;
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * Get all notifications for an investor, newest first.
     */
    getNotifications(investorId: string): InvestorNotification[] {
        return this.notifications.filter((n) => n.investorId === investorId);
    }

    /**
     * Mark a single notification as read.
     */
    markAsRead(notificationId: string): void {
        const n = this.notifications.find((x) => x.id === notificationId);
        if (n) {
            n.read = true;
            this.emit();
        }
    }

    /**
     * Mark all notifications for an investor as read.
     */
    markAllAsRead(investorId: string): void {
        let changed = false;
        for (const n of this.notifications) {
            if (n.investorId === investorId && !n.read) {
                n.read = true;
                changed = true;
            }
        }
        if (changed) {
            this.emit();
        }
    }

    /**
     * Get count of unread notifications for an investor.
     */
    getUnreadCount(investorId: string): number {
        return this.notifications.filter(
            (n) => n.investorId === investorId && !n.read,
        ).length;
    }

    // ── EVENT INGESTION ──────────────────────────────────────────────────

    /**
     * Ingest a milestone verification event for one or more investors.
     */
    ingestMilestoneEvent(
        investorIds: string[],
        input: MilestoneEventInput,
    ): InvestorNotification[] {
        const payload = mapMilestoneEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest a distribution event for one or more investors.
     */
    ingestDistributionEvent(
        investorIds: string[],
        input: DistributionEventInput,
    ): InvestorNotification[] {
        const payload = mapDistributionEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest an evidence availability event for one or more investors.
     */
    ingestEvidenceEvent(
        investorIds: string[],
        input: EvidenceEventInput,
    ): InvestorNotification[] {
        const payload = mapEvidenceEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest a capital release event for one or more investors.
     */
    ingestReleaseEvent(
        investorIds: string[],
        input: ReleaseEventInput,
    ): InvestorNotification[] {
        const payload = mapReleaseEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest a portfolio-level event for one or more investors.
     */
    ingestPortfolioEvent(
        investorIds: string[],
        input: PortfolioEventInput,
    ): InvestorNotification[] {
        const payload = mapPortfolioEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest an approval workflow event for one or more investors.
     */
    ingestApprovalEvent(
        investorIds: string[],
        input: ApprovalEventInput,
    ): InvestorNotification[] {
        const payload = mapApprovalEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest an e-signature event for one or more investors.
     */
    ingestSignatureEvent(
        investorIds: string[],
        input: SignatureEventInput,
    ): InvestorNotification[] {
        const payload = mapSignatureEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest a finance approval event for one or more investors.
     */
    ingestFinanceApprovalEvent(
        investorIds: string[],
        input: FinanceApprovalEventInput,
    ): InvestorNotification[] {
        const payload = mapFinanceApprovalEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Ingest a data room access event for one or more investors.
     */
    ingestDataRoomAccessEvent(
        investorIds: string[],
        input: DataRoomAccessEventInput,
    ): InvestorNotification[] {
        const payload = mapDataRoomAccessEvent(input);
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    /**
     * Broadcast a custom notification to multiple investors.
     */
    broadcast(
        investorIds: string[],
        payload: InvestorNotificationPayload,
    ): InvestorNotification[] {
        return investorIds.map((id) => this.createNotification(id, payload));
    }

    private emit(): void {
        for (const listener of this.listeners) {
            listener();
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════════

let instance: InvestorNotificationService | null = null;

export function getInvestorNotificationService(): InvestorNotificationService {
    if (!instance) {
        instance = new InvestorNotificationService();
        // Seed demo data so the notification panel is never empty in demo mode.
        // Uses both the real demo user id ('8') and the fallback id ('demo-investor').
        instance.seedDemoData(['8', 'demo-investor', 'investor-user-001']);
    }
    return instance;
}

export function resetInvestorNotificationService(): void {
    instance = null;
}
